import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import ProFeatureGate from '@/components/ProFeatureGate';
import { useMonth } from '@/contexts/MonthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, DollarSign, Trash2, Edit2, ChevronLeft, ChevronRight, Calendar, Repeat } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { toast } from 'sonner';
import { fmtMoney } from '@/lib/currency';
import { useDisplayCurrency } from '@/hooks/useDisplayCurrency';

const INCOME_TYPES = ['Salary', 'Bonus', 'Freelance', 'Business Income', 'Rental Income', 'Investment Income', 'Other Income'];
const FREQUENCIES = ['monthly', 'biweekly', 'annual', 'one-time'];
const PIE_COLORS = ['hsl(136,18%,35%)', 'hsl(35,75%,55%)', 'hsl(195,58%,48%)', 'hsl(15,68%,55%)', 'hsl(260,40%,55%)', 'hsl(50,75%,52%)', 'hsl(0,60%,55%)'];
const fmt = (n) => fmtMoney(n);
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const getML = (y, m) => `${MONTH_NAMES[parseInt(m, 10) - 1]} ${y}`;
const navM = (y, m, d) => { let mi = parseInt(m, 10) + d, yi = parseInt(y, 10); while (mi > 12) { mi -= 12; yi++; } while (mi < 1) { mi += 12; yi--; } return { year: String(yi), month: String(mi).padStart(2, '0') }; };

export default function IncomePage() {
  useDisplayCurrency(); // re-render when user changes base currency
  const { user } = useAuth();
  const [incomes, setIncomes] = useState([]);
  const [summary, setSummary] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const { year: currentYear, month: currentMonth, setMonth } = useMonth();

  const load = useCallback(async () => {
    if (!currentYear || !currentMonth) return;
    if (!user?.has_pro_access) { setLoading(false); return; }
    setLoading(true);
    try {
      const params = { year: currentYear, month: currentMonth };
      const [listRes, sumRes] = await Promise.all([
        api.get('/api/income', { params }),
        api.get('/api/income/summary', { params }),
      ]);
      setIncomes(listRes.data);
      setSummary(sumRes.data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [currentYear, currentMonth, user?.has_pro_access]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await api.delete(`/api/income/${id}`); toast.success('Deleted'); load(); } catch { toast.error('Failed'); }
  };

  const handleGenerateRecurring = async () => {
    if (!currentYear || !currentMonth) return;
    try {
      const { data } = await api.post(`/api/income/generate-recurring?target_month=${currentYear}-${currentMonth}`);
      toast.success(data.message);
      load();
    } catch { toast.error('Failed to generate'); }
  };

  const handlePrev = () => { if (currentYear && currentMonth) { const n = navM(currentYear, currentMonth, -1); setMonth(n.year, n.month); } };
  const handleNext = () => { if (currentYear && currentMonth) { const n = navM(currentYear, currentMonth, 1); setMonth(n.year, n.month); } };
  const monthLabel = currentYear && currentMonth ? getML(currentYear, currentMonth) : '';
  const monthTotal = incomes.reduce((s, i) => s + (i.amount || 0), 0);

  // Item §3c — Income sources pie data
  const pieData = useMemo(() => {
    const byType = {};
    incomes.forEach((i) => { byType[i.source || 'Unknown'] = (byType[i.source || 'Unknown'] || 0) + (i.amount || 0); });
    return Object.entries(byType).map(([name, value]) => ({ name, value: Math.round(value) }));
  }, [incomes]);

  return (
    <ProFeatureGate feature="Income Tracking">
    <div className="space-y-6" data-testid="income-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight font-['Outfit']">Income</h1>
          <p className="text-sm text-muted-foreground mt-1">Track your income sources</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={handlePrev}><ChevronLeft className="h-4 w-4" /></Button>
          <span className="text-sm font-medium px-2 min-w-[100px] text-center">{monthLabel}</span>
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={handleNext}><ChevronRight className="h-4 w-4" /></Button>
          <Dialog open={showAdd || !!editItem} onOpenChange={(o) => { if (!o) { setShowAdd(false); setEditItem(null); } else setShowAdd(true); }}>
            <DialogTrigger asChild><Button size="sm" data-testid="add-income-btn"><Plus className="h-4 w-4 mr-1" />Add Income</Button></DialogTrigger>
            <DialogContent><DialogHeader><DialogTitle className="font-['Outfit']">{editItem ? 'Edit Income' : 'Add Income'}</DialogTitle></DialogHeader><IncomeForm item={editItem} defaultDate={`${currentYear}-${currentMonth}-01`} onSave={async () => { await load(); setShowAdd(false); setEditItem(null); }} /></DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border border-border shadow-none" data-testid="income-total-card">
          <CardContent className="p-4">
            <p className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">Total for {monthLabel}</p>
            <p className="text-2xl font-semibold tracking-tight font-['Outfit'] tabular-nums mt-1 text-green-600 dark:text-green-400">{fmt(monthTotal)}</p>
          </CardContent>
        </Card>
        <Card className="border border-border shadow-none">
          <CardContent className="p-4">
            <p className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">Sources</p>
            <p className="text-2xl font-semibold tracking-tight font-['Outfit'] tabular-nums mt-1">{incomes.length}</p>
          </CardContent>
        </Card>
        <Card className="border border-border shadow-none">
          <CardContent className="p-4">
            <p className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">Recurring</p>
            <p className="text-2xl font-semibold tracking-tight font-['Outfit'] tabular-nums mt-1">{incomes.filter(i => i.is_recurring).length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Income sources pie (Item §3c) */}
      {pieData.length > 0 && (
        <Card className="border border-border shadow-none" data-testid="income-sources-pie">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Income breakdown — {monthLabel}</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={45} outerRadius={80} dataKey="value" stroke="none" paddingAngle={2}
                       label={(p) => `${Math.round((p.value / monthTotal) * 100)}%`} labelLine={false}>
                    {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => fmt(v)} contentStyle={{ fontSize: '12px', borderRadius: '6px', border: '1px solid hsl(var(--border))', background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }} itemStyle={{ color: 'hsl(var(--card-foreground))' }} labelStyle={{ color: 'hsl(var(--card-foreground))' }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-1.5">
                {pieData.map((p, i) => (
                  <div key={p.name} className="flex items-center justify-between text-xs" data-testid={`income-source-row-${p.name}`}>
                    <div className="flex items-center gap-2 truncate">
                      <span className="h-2.5 w-2.5 rounded-sm flex-shrink-0" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                      <span className="truncate">{p.name}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-muted-foreground tabular-nums">{Math.round((p.value / monthTotal) * 100)}%</span>
                      <span className="font-medium tabular-nums">{fmt(p.value)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* List */}
      <Card className="border border-border shadow-none">
        <CardHeader className="pb-3"><CardTitle className="text-sm font-medium">Income Entries &mdash; {monthLabel}</CardTitle></CardHeader>
        <CardContent>
          {loading ? <div className="space-y-2">{[...Array(3)].map((_, i) => <div key={i} className="h-14 bg-muted rounded animate-pulse" />)}</div>
          : incomes.length === 0 ? <p className="text-sm text-muted-foreground py-8 text-center">No income for {monthLabel}. Add your first income source or apply recurring.</p>
          : (
            <div className="space-y-2">
              {incomes.map((inc) => (
                <div key={inc._id} className="flex items-center justify-between p-3 rounded-md border border-border bg-background" data-testid={`income-item-${inc._id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-8 w-8 rounded-md bg-primary/10 flex items-center justify-center shrink-0"><DollarSign className="h-4 w-4 text-primary" /></div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium truncate">{inc.source}</p>
                        {inc.is_recurring && <Repeat className="h-3 w-3 text-primary" />}
                        {inc.is_virtual && <Badge variant="outline" className="text-[10px] h-4">auto</Badge>}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Badge variant="secondary" className="text-xs">{inc.income_type}</Badge>
                        <span className="text-xs text-muted-foreground">{inc.frequency} &middot; {inc.date}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-sm font-semibold tabular-nums text-green-600 dark:text-green-400">{fmt(inc.amount)}</span>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditItem(inc)} data-testid={`edit-income-${inc._id}`} title={inc.is_virtual ? 'Override for this month' : 'Edit'}><Edit2 className="h-3 w-3" /></Button>
                    {!inc.is_virtual && (
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(inc._id)} data-testid={`delete-income-${inc._id}`}><Trash2 className="h-3 w-3" /></Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
    </ProFeatureGate>
  );
}

function IncomeForm({ item, defaultDate, onSave }) {
  const [form, setForm] = useState({
    source: item?.source || '', income_type: item?.income_type || 'Salary',
    amount: item?.amount?.toString() || '', frequency: item?.frequency || 'monthly',
    date: item?.date || defaultDate || new Date().toISOString().split('T')[0],
    is_recurring: item?.is_recurring || false, recurring_end_date: item?.recurring_end_date || '',
    tax_enabled: item?.tax_enabled || false,
    tax_percentage: item?.tax_percentage?.toString() || '',
    notes: item?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isSalary = form.income_type === 'Salary';

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = {
        ...form,
        amount: parseFloat(form.amount),
        recurring_end_date: form.recurring_end_date || null,
        tax_enabled: isSalary && form.tax_enabled,
        tax_percentage: isSalary && form.tax_enabled ? (parseFloat(form.tax_percentage) || 0) : 0,
      };
      
      if (item && !String(item._id).startsWith('recurring_')) {
        // Editing an existing real income entry
        await api.put(`/api/income/${item._id}`, payload); 
        toast.success('Updated');
      } else if (item && String(item._id).startsWith('recurring_')) {
        // Virtual recurring entry being edited
        // Extract the original recurring income ID from the virtual ID format: recurring_{originalId}_{month}_{day}
        const parts = String(item._id).split('_');
        const originalId = parts[1]; // Get the original recurring income ID
        
        // Check if user is trying to end this recurring income
        if (form.recurring_end_date && form.is_recurring) {
          // Update the original recurring income's end date
          await api.put(`/api/income/${originalId}`, {
            ...item,
            recurring_end_date: form.recurring_end_date,
            is_recurring: true,
          });
          toast.success('Recurring income end date updated');
        } else {
          // Create a one-off override for this specific month
          await api.post('/api/income', { ...payload, is_recurring: false }); 
          toast.success('Override saved for this month');
        }
      } else {
        // Creating a new income entry (preserve user's recurring choice)
        await api.post('/api/income', payload); 
        toast.success('Income added');
      }
      await onSave();
    } catch (err) { 
      console.error('Income save error:', err);
      toast.error('Failed'); 
    } finally { 
      setSaving(false); 
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="space-y-1"><Label className="text-xs">Source</Label><Input data-testid="income-source-input" value={form.source} onChange={(e) => set('source', e.target.value)} placeholder="e.g., Company XYZ" required /></div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1"><Label className="text-xs">Type</Label><Select value={form.income_type} onValueChange={(v) => set('income_type', v)}><SelectTrigger data-testid="income-type-select"><SelectValue /></SelectTrigger><SelectContent>{INCOME_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
        <div className="space-y-1"><Label className="text-xs">Amount</Label><Input data-testid="income-amount-input" type="number" step="0.01" value={form.amount} onChange={(e) => set('amount', e.target.value)} required /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1"><Label className="text-xs">Frequency</Label><Select value={form.frequency} onValueChange={(v) => set('frequency', v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{FREQUENCIES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent></Select></div>
        <div className="space-y-1"><Label className="text-xs">Date</Label><Input data-testid="income-date-input" type="date" value={form.date} onChange={(e) => set('date', e.target.value)} required /></div>
      </div>
      <div className="flex items-center justify-between p-3 rounded-md border border-border">
        <div className="flex items-center gap-2"><Repeat className="h-4 w-4 text-primary" /><div><p className="text-sm font-medium">Recurring Income</p><p className="text-xs text-muted-foreground">Auto-apply to future months</p></div></div>
        <Switch checked={form.is_recurring} onCheckedChange={(v) => set('is_recurring', v)} data-testid="income-recurring-toggle" />
      </div>
      {form.is_recurring && (
        <div className="space-y-1"><Label className="text-xs">End Date (optional)</Label><Input type="date" value={form.recurring_end_date} onChange={(e) => set('recurring_end_date', e.target.value)} placeholder="Leave empty for indefinite" /></div>
      )}
      {isSalary && (
        <div className="p-3 rounded-md border border-border space-y-2" data-testid="income-tax-block">
          <div className="flex items-center justify-between">
            <div><p className="text-sm font-medium">Track Tax (contractor mode)</p><p className="text-xs text-muted-foreground">For non-PAYE salary. Calculates Net Income + YTD tax owed.</p></div>
            <Switch checked={form.tax_enabled} onCheckedChange={(v) => set('tax_enabled', v)} data-testid="income-tax-toggle" />
          </div>
          {form.tax_enabled && (
            <div className="flex items-center gap-2"><Label className="text-xs">Tax %</Label><Input data-testid="income-tax-percentage" type="number" step="0.5" min="0" max="100" value={form.tax_percentage} onChange={(e) => set('tax_percentage', e.target.value)} className="w-24 h-8 text-sm" required /><span className="text-xs text-muted-foreground">of this month&apos;s gross</span></div>
          )}
        </div>
      )}
      <div className="space-y-1"><Label className="text-xs">Notes</Label><Input value={form.notes} onChange={(e) => set('notes', e.target.value)} /></div>
      <Button type="submit" className="w-full" disabled={saving} data-testid="income-submit-btn">{saving ? 'Saving...' : item ? 'Update' : 'Add Income'}</Button>
    </form>
  );
}
